import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { MerchProduct } from '@juvo/schemas';
import { formatUsd, pickLocale } from '@juvo/utils';
import { store } from '../store.js';
import { publicSectionStyles } from '../styles/public.js';
import '../layouts/site-chrome.js';

@customElement('jv-merch-page')
export class JvMerchPage extends LitElement {
  static styles = [
    publicSectionStyles,
    css`
      h1 {
        font-size: var(--jv-fs-display);
        margin: 0 0 0.6rem;
      }
      .intro {
        color: var(--jv-color-muted);
        max-width: 40rem;
        margin-bottom: 2rem;
      }
    `,
  ];

  @state() private products: MerchProduct[] = [];
  @state() private addedSku = '';

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    store.client.getMerch().then((products) => {
      this.products = products;
    });
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  render() {
    const t = store.t.bind(store);
    return html`
      <jv-page>
        <h1>${t('merch.pageTitle')}</h1>
        <p class="intro">${t('merch.pageIntro')}</p>
        <div class="cards thirds">
          ${this.products.map(
            (product) => html`
              <jv-card image=${product.image} alt=${pickLocale(product.name, store.locale)}>
                <jv-badge>${product.sku}</jv-badge>
                <h3 class="card-title">${pickLocale(product.name, store.locale)}</h3>
                <p class="muted">${pickLocale(product.description, store.locale)}</p>
                <div class="card-meta">
                  <span class="price">${formatUsd(product.priceUsd)}</span>
                  <jv-button size="small" variant="ghost" @click=${() => this.add(product.sku)}>
                    ${this.addedSku === product.sku ? t('merch.added') : t('merch.add')}
                  </jv-button>
                </div>
              </jv-card>
            `,
          )}
        </div>
      </jv-page>
    `;
  }

  private add(sku: string) {
    store.addToCart(sku);
    this.addedSku = sku;
  }
}

@customElement('jv-cart-page')
export class JvCartPage extends LitElement {
  static styles = [
    publicSectionStyles,
    css`
      h1 {
        font-size: var(--jv-fs-display);
        margin: 0 0 1rem;
      }
      .layout {
        display: grid;
        gap: 1.5rem;
      }
      @media (min-width: 860px) {
        .layout {
          grid-template-columns: 1.2fr 1fr;
        }
      }
      .line {
        display: grid;
        grid-template-columns: 1fr auto auto;
        gap: 0.75rem;
        align-items: center;
      }
      input[type='number'] {
        width: 4.2rem;
        min-height: 2.4rem;
        border: var(--jv-border-w) solid var(--jv-color-border);
        padding: 0.3rem 0.45rem;
        font-family: inherit;
      }
      form {
        display: grid;
        gap: 0.75rem;
      }
      .ok {
        color: var(--jv-color-accent);
      }
      .err {
        color: var(--jv-color-danger);
      }
      .total {
        font-size: 1.3rem;
        font-weight: 700;
        color: var(--jv-color-accent);
      }
    `,
  ];

  @state() private products: MerchProduct[] = [];
  @state() private name = '';
  @state() private email = '';
  @state() private city = '';
  @state() private note = '';
  @state() private message = '';
  @state() private error = '';

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    store.client.getMerch().then((products) => {
      this.products = products;
    });
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  private lines() {
    return store.cart
      .map((line) => {
        const product = this.products.find((item) => item.sku === line.sku);
        return product ? { ...line, product } : null;
      })
      .filter((row): row is { sku: string; qty: number; product: MerchProduct } => !!row);
  }

  render() {
    const t = store.t.bind(store);
    const lines = this.lines();
    const total = lines.reduce((sum, line) => sum + line.product.priceUsd * line.qty, 0);
    if (store.cart.length === 0) {
      return html`
        <jv-page>
          <h1>${t('merch.cartTitle')}</h1>
          <jv-empty-state heading=${t('merch.empty')}>
            <jv-button href="/merch">${t('merch.browse')}</jv-button>
          </jv-empty-state>
        </jv-page>
      `;
    }
    return html`
      <jv-page>
        <h1>${t('merch.cartTitle')}</h1>
        <div class="layout">
          <jv-card>
            ${lines.map(
              (line) => html`
                <div class="line">
                  <div>
                    <strong>${pickLocale(line.product.name, store.locale)}</strong>
                    <p class="muted">${formatUsd(line.product.priceUsd)}</p>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    .value=${String(line.qty)}
                    @change=${(e: Event) =>
                      store.setCartQty(line.sku, Number((e.target as HTMLInputElement).value))}
                    aria-label=${t('merch.qty')}
                  />
                  <span class="price">${formatUsd(line.product.priceUsd * line.qty)}</span>
                </div>
              `,
            )}
            <p class="total">${t('merch.total')}: ${formatUsd(total)}</p>
          </jv-card>
          <jv-card>
            <form @submit=${this.onCheckout}>
              <jv-form-field
                label=${t('donate.name')}
                name="name"
                required
                .value=${this.name}
                @jv-input=${(e: CustomEvent) => (this.name = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label=${t('donate.email')}
                name="email"
                type="email"
                required
                .value=${this.email}
                @jv-input=${(e: CustomEvent) => (this.email = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label=${t('merch.city')}
                name="city"
                .value=${this.city}
                @jv-input=${(e: CustomEvent) => (this.city = e.detail.value)}
              ></jv-form-field>
              <jv-form-field
                label=${t('merch.note')}
                name="note"
                multiline
                .value=${this.note}
                @jv-input=${(e: CustomEvent) => (this.note = e.detail.value)}
              ></jv-form-field>
              <jv-button type="submit">${t('merch.checkout')}</jv-button>
              ${this.message ? html`<p class="ok">${this.message}</p>` : ''}
              ${this.error ? html`<p class="err">${this.error}</p>` : ''}
            </form>
          </jv-card>
        </div>
      </jv-page>
    `;
  }

  private onCheckout = async (event: Event) => {
    event.preventDefault();
    this.error = '';
    try {
      await store.client.submitMerchOrder({
        name: this.name,
        email: this.email,
        city: this.city || undefined,
        note: this.note || undefined,
        items: store.cart.map((line) => ({ sku: line.sku, qty: line.qty })),
      });
      store.clearCart();
      this.message = store.t('merch.thanks');
    } catch {
      this.error = store.t('merch.error');
    }
  };
}
